import { useRecoilState, useSetRecoilState } from 'recoil';
import { Product, ProductId } from 'utils/Product';
import { produce } from 'immer';
import { cartAtom } from './cart-state';

const cartActions = {
  useAddProduct: () => {
    const [cartState, setCartState] = useRecoilState(cartAtom);

    return (product: Product) => {
      if (cartState[product.id]) {
        setCartState(produce(cartState, draft => {
          draft[product.id].cartQuantity = Math.min(
            cartState[product.id].cartQuantity + 1,
            cartState[product.id].product.quantity,
          );
        }));
      } else {
        setCartState(produce(cartState, draft => {
          draft[product.id] = {
            product,
            cartQuantity: 1,
          };
        }));
      }
    };
  },
  useChangeQuantity: () => {
    const [cartState, setCartState] = useRecoilState(cartAtom);

    return ({ id, modQuantity }: { id: ProductId, modQuantity: number }) => {
      if (!cartState[id]) throw Error('Can\'t change quantity of cart that doesn\'t already exist in cart, use addProduct first');

      setCartState(produce(cartState, draft => {
        draft[id].cartQuantity = Math.max(
          0,
          Math.min(cartState[id].cartQuantity + modQuantity, cartState[id].product.quantity),
        );
        if (draft[id].cartQuantity === 0) delete draft[id];
      }));
    };
  },
  useSetQuantity: () => {
    const [cartState, setCartState] = useRecoilState(cartAtom);

    return ({ id, newQuantity }: { id: ProductId, newQuantity: number}) => {
      if (!cartState[id]) throw Error('Can\'t set quantity of cart that doesn\'t already exist in cart, use addProduct first');
      setCartState(produce(cartState, draft => {
        draft[id].cartQuantity = Math.max(0, Math.min(newQuantity, cartState[id].product.quantity));
        if (draft[id].cartQuantity === 0) delete draft[id];
      }));
    };
  },
  useRemoveItem: () => {
    const [cartState, setCartState] = useRecoilState(cartAtom);
    return (id: ProductId) => {
      setCartState(produce(cartState, draft => {
        delete draft[id];
      }));
    };
  },
  useEmptyCart: () => {
    const setCartState = useSetRecoilState(cartAtom);
    return () => {
      setCartState({});
    };
  },
};

const useCartActions = () => ({
  addCartProduct: cartActions.useAddProduct(),
  changeCartQuantity: cartActions.useChangeQuantity(),
  setCartQuantity: cartActions.useSetQuantity(),
  removeCartItem: cartActions.useRemoveItem(),
  emptyCart: cartActions.useEmptyCart(),
});

export default useCartActions;
